import { Injectable } from '@angular/core';

export type AuthAuditEventType =
  | 'login_success'
  | 'login_failed'
  | 'logout'
  | 'token_refresh_success'
  | 'token_refresh_failed'
  | 'forbidden'
  | 'session_timeout';

export interface AuthAuditEvent {
  type: AuthAuditEventType;
  timestamp: Date;
  userId?: string;
  email?: string;
  reason?: string;
  details?: Record<string, unknown>;
}

/**
 * AuthAuditService: Records authentication-related events for audit purposes.
 * Events are stored in-memory and logged to the console in development.
 * In production, events could be sent to a backend audit endpoint.
 */
@Injectable({ providedIn: 'root' })
export class AuthAuditService {
  private events: AuthAuditEvent[] = [];

  /** Maximum number of events to retain in memory. */
  private readonly maxEvents = 100;

  record(
    type: AuthAuditEventType,
    data?: Partial<Omit<AuthAuditEvent, 'type' | 'timestamp'>>
  ): void {
    const event: AuthAuditEvent = {
      type,
      timestamp: new Date(),
      ...data
    };

    this.events.push(event);
    if (this.events.length > this.maxEvents) {
      this.events.shift();
    }

    console.debug(`[AuthAudit] ${type}`, event);
  }

  /** Returns a copy of all recorded events. */
  getEvents(): AuthAuditEvent[] {
    return [...this.events];
  }

  /** Returns the most recent N events. */
  getRecentEvents(n = 10): AuthAuditEvent[] {
    return this.events.slice(-n);
  }

  /** Clears all recorded events. */
  clearEvents(): void {
    this.events = [];
  }
}
